import React, { useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import CollapsiblePanel from "./CollapsiblePanel";
import {
  Sparkles, Activity, Microscope, FileText, CheckCircle2, AlertTriangle,
  TrendingUp, Brain, BookOpen, ExternalLink, Lightbulb, MessageSquare, ArrowRight,
} from "lucide-react";

/**
 * DemoExamplesPanel — Shows 3 realistic demo examples so new users
 * understand the AI output BEFORE uploading their own data.
 * Visible for all users, auto-expanded for new accounts.
 */

const TABS = [
  { id: "clinical", icon: Activity, bg: "Клиничен AI анализ", en: "Clinical AI Analysis" },
  { id: "chat", icon: Brain, bg: "AI чат + Научен слой", en: "AI Chat + Evidence" },
  { id: "lab", icon: FileText, bg: "Анализ на изследвания", en: "Lab Analysis" },
];

// =============== DEMO 1: Clinical AI Analysis ===============
function ClinicalDemo({ bg, isDark }) {
  return (
    <div className="space-y-3" data-testid="demo-clinical">
      {/* Header with Endo Score */}
      <div className={`p-3 rounded-lg bg-gradient-to-r from-[#0EA5E9]/10 to-[#6366F1]/10 border ${isDark ? "border-[#0EA5E9]/30" : "border-[#0EA5E9]/20"}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-[10px] font-semibold uppercase tracking-wide ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {bg ? "Endo Score" : "Endo Score"}
            </p>
            <p className="text-3xl font-bold bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] bg-clip-text text-transparent">72/100</p>
            <p className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              {bg ? "Умерен възпалителен профил" : "Moderate inflammatory profile"}
            </p>
          </div>
          <div className="text-right">
            <p className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>DIL</p>
            <p className="text-lg font-bold text-amber-500">+128</p>
            <p className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              {bg ? "над цел" : "above target"}
            </p>
          </div>
        </div>
      </div>

      {/* Key insights */}
      <div>
        <h4 className={`text-xs font-bold mb-1.5 flex items-center gap-1.5 ${isDark ? "text-white" : "text-gray-800"}`}>
          <Lightbulb className="w-3.5 h-3.5 text-amber-500" />
          {bg ? "Ключови наблюдения" : "Key insights"}
        </h4>
        <ul className="space-y-1.5">
          {[
            bg ? "CRP 4.2 mg/L — леко завишен, типично за метаболитен синдром" : "CRP 4.2 mg/L — mildly elevated, typical of metabolic syndrome",
            bg ? "HOMA-IR 2.8 — ранна инсулинова резистентност (цел <2.0)" : "HOMA-IR 2.8 — early insulin resistance (target <2.0)",
            bg ? "Витамин D 22 ng/mL — недостатъчен; корелация с умора в симптомите" : "Vitamin D 22 ng/mL — insufficient; correlates with reported fatigue",
          ].map((insight, i) => (
            <li key={`cl-${i}`} className={`text-[11px] flex items-start gap-1.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              <span className="w-1 h-1 rounded-full bg-[#6366F1] mt-1.5 flex-shrink-0" />
              <span>{insight}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Recommendations */}
      <div className={`p-2.5 rounded-lg ${isDark ? "bg-emerald-900/20" : "bg-emerald-50"}`}>
        <h4 className={`text-xs font-bold mb-1.5 flex items-center gap-1.5 ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>
          <CheckCircle2 className="w-3.5 h-3.5" />
          {bg ? "Персонализирани препоръки" : "Personalized recommendations"}
        </h4>
        <ul className="space-y-1 pl-4">
          {[
            bg ? "Намалете рафинираните въглехидрати до <40г/ден за 6 седмици" : "Reduce refined carbs to <40g/day for 6 weeks",
            bg ? "Добавете Омега-3 2г/ден (EPA+DHA) — ще свали CRP с ~30%" : "Add Omega-3 2g/day (EPA+DHA) — will reduce CRP ~30%",
            bg ? "D3 5000 IU/ден + магнезий глицинат 300mg вечер" : "D3 5000 IU/day + magnesium glycinate 300mg at bedtime",
            bg ? "Заменете закуската с богата на протеин (~30г) за стабилизиране на глюкозата" : "Swap breakfast for protein-rich (~30g) to stabilize glucose",
          ].map((rec, i) => (
            <li key={`cr-${i}`} className={`text-[11px] list-disc ${isDark ? "text-emerald-200" : "text-emerald-800"}`}>
              {rec}
            </li>
          ))}
        </ul>
      </div>

      {/* Trend */}
      <div className={`p-2 rounded-lg flex items-center gap-2 ${isDark ? "bg-[hsl(223,47%,10%)]" : "bg-gray-50"}`}>
        <TrendingUp className="w-4 h-4 text-[#10B981]" />
        <p className={`text-[11px] ${isDark ? "text-gray-300" : "text-gray-600"}`}>
          <span className="font-semibold">{bg ? "Прогноза: " : "Forecast: "}</span>
          {bg
            ? "При спазване → Endo Score ~82 за 8 седмици"
            : "With adherence → Endo Score ~82 in 8 weeks"}
        </p>
      </div>
    </div>
  );
}

// =============== DEMO 2: AI Chat + Evidence Engine ===============
function ChatDemo({ bg, isDark }) {
  return (
    <div className="space-y-3" data-testid="demo-chat">
      {/* User question bubble */}
      <div className="flex justify-end">
        <div className={`max-w-[85%] p-2.5 rounded-2xl rounded-br-sm ${isDark ? "bg-[#6366F1]/20 text-gray-200" : "bg-[#6366F1]/10 text-gray-800"}`}>
          <p className="text-[11px]">
            {bg
              ? "Помага ли берберин на инсулинова резистентност?"
              : "Does berberine help with insulin resistance?"}
          </p>
        </div>
      </div>

      {/* AI answer */}
      <div className={`p-2.5 rounded-lg ${isDark ? "bg-[hsl(223,47%,10%)]" : "bg-gray-50"}`}>
        <div className="flex items-start gap-2 mb-1.5">
          <div className="w-5 h-5 rounded-lg bg-gradient-to-br from-[#0EA5E9] to-[#6366F1] flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-3 h-3 text-white" />
          </div>
          <p className={`text-[10px] font-semibold ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            {bg ? "Nutramilo AI" : "Nutramilo AI"}
          </p>
        </div>

        <p className={`text-[11px] leading-relaxed mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
          {bg
            ? "Да, берберин (500mg 2-3×/ден) показва сравнима ефективност с метформин за HbA1c и HOMA-IR"
            : "Yes, berberine (500mg 2-3×/day) shows efficacy comparable to metformin for HbA1c and HOMA-IR"}
          {" "}
          <span className="inline-flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-semibold rounded bg-emerald-100 text-emerald-700">
            ● 87%
          </span>
          .
          {" "}
          {bg
            ? "При Вашия профил (PCOS + HOMA-IR 2.8) е подходящ кандидат"
            : "For your profile (PCOS + HOMA-IR 2.8) it's a reasonable candidate"}
          {" "}
          <span className="inline-flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-semibold rounded bg-amber-100 text-amber-700">
            ● 64%
          </span>
          .
        </p>
      </div>

      {/* Evidence Layer - Consensus bar */}
      <div className={`p-2.5 rounded-lg border ${isDark ? "bg-[#0EA5E9]/10 border-[#0EA5E9]/30" : "bg-blue-50 border-blue-200"}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Microscope className="w-3.5 h-3.5 text-[#0EA5E9]" />
            <p className={`text-[11px] font-bold ${isDark ? "text-white" : "text-gray-800"}`}>
              {bg ? "Научна обосновка" : "Scientific evidence"}
            </p>
          </div>
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500 text-white font-semibold">
            {bg ? "Висока увереност" : "High confidence"}
          </span>
        </div>

        {/* Consensus bar */}
        <div className="mb-2">
          <div className="flex items-center justify-between mb-1">
            <p className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              {bg ? "Научен консенсус" : "Scientific consensus"}
            </p>
            <p className="text-[10px] font-bold text-emerald-500">87%</p>
          </div>
          <div className={`h-1.5 rounded-full ${isDark ? "bg-gray-800" : "bg-gray-200"}`}>
            <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400" style={{ width: "87%" }} />
          </div>
        </div>

        {/* Verified studies */}
        <div>
          <p className={`text-[10px] font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
            {bg ? "Проверени проучвания (3 от 8)" : "Verified studies (3 of 8)"}:
          </p>
          <div className="space-y-1">
            {[
              { title: bg ? "Meta-анализ: Berberine vs Metformin for T2DM (Yin J. et al.)" : "Meta-analysis: Berberine vs Metformin for T2DM (Yin J. et al.)", year: "2008", type: "Meta-analysis", pmid: "18397984" },
              { title: bg ? "RCT: Berberine + lifestyle for PCOS & IR (An Y. et al.)" : "RCT: Berberine + lifestyle for PCOS & IR (An Y. et al.)", year: "2014", type: "RCT", pmid: "24517201" },
              { title: bg ? "Berberine effects on HOMA-IR in metabolic syndrome (Dong H. et al.)" : "Berberine effects on HOMA-IR in metabolic syndrome (Dong H. et al.)", year: "2015", type: "Cohort", pmid: "25796599" },
            ].map((s) => (
              <div key={s.pmid} className={`p-1.5 rounded text-[10px] flex items-start justify-between gap-1.5 ${isDark ? "bg-[hsl(223,47%,8%)]" : "bg-white"}`}>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${isDark ? "text-gray-200" : "text-gray-800"}`}>{s.title}</p>
                  <p className={`${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    <span className="text-[9px] font-semibold">{s.type}</span> · {s.year} · PMID {s.pmid}
                  </p>
                </div>
                <span className="text-emerald-500 flex-shrink-0">✓</span>
              </div>
            ))}
          </div>
          <p className={`text-[9px] mt-1.5 italic ${isDark ? "text-gray-500" : "text-gray-500"}`}>
            {bg ? "Източници: PubMed, Semantic Scholar" : "Sources: PubMed, Semantic Scholar"}
          </p>
        </div>
      </div>
    </div>
  );
}

// =============== DEMO 3: Lab Analysis ===============
function LabDemo({ bg, isDark }) {
  const markers = [
    { name: bg ? "Глюкоза на гладно" : "Fasting glucose", val: "5.8", unit: "mmol/L", ref: "3.9–5.6", status: "warn", note: bg ? "Граничен" : "Borderline" },
    { name: "HbA1c", val: "5.9", unit: "%", ref: "<5.7", status: "warn", note: bg ? "Пре-диабет" : "Pre-diabetes" },
    { name: "LDL", val: "3.7", unit: "mmol/L", ref: "<3.0", status: "warn", note: bg ? "Леко високо" : "Slightly high" },
    { name: "HDL", val: "1.4", unit: "mmol/L", ref: ">1.0", status: "ok", note: bg ? "Добър" : "Good" },
    { name: "CRP", val: "4.2", unit: "mg/L", ref: "<3.0", status: "warn", note: bg ? "Лека възпалителна активност" : "Mild inflammation" },
    { name: bg ? "Витамин D" : "Vitamin D", val: "22", unit: "ng/mL", ref: "30–80", status: "bad", note: bg ? "Недостатъчен" : "Insufficient" },
    { name: "TSH", val: "2.1", unit: "mIU/L", ref: "0.4–4.0", status: "ok", note: bg ? "В норма" : "Normal" },
    { name: bg ? "Феритин" : "Ferritin", val: "45", unit: "ng/mL", ref: "30–300", status: "ok", note: bg ? "В норма" : "Normal" },
  ];

  const statusColor = (s) => {
    if (s === "ok") return isDark ? "text-emerald-400 bg-emerald-900/30" : "text-emerald-700 bg-emerald-100";
    if (s === "warn") return isDark ? "text-amber-400 bg-amber-900/30" : "text-amber-700 bg-amber-100";
    return isDark ? "text-red-400 bg-red-900/30" : "text-red-700 bg-red-100";
  };

  return (
    <div className="space-y-3" data-testid="demo-lab">
      {/* Summary */}
      <div className={`p-2.5 rounded-lg ${isDark ? "bg-[#6366F1]/10 border border-[#6366F1]/30" : "bg-indigo-50 border border-indigo-200"}`}>
        <div className="flex items-center gap-1.5 mb-1">
          <Sparkles className="w-3.5 h-3.5 text-[#6366F1]" />
          <p className={`text-xs font-bold ${isDark ? "text-white" : "text-gray-800"}`}>
            {bg ? "AI обобщение" : "AI summary"}
          </p>
        </div>
        <p className={`text-[11px] leading-relaxed ${isDark ? "text-gray-300" : "text-gray-700"}`}>
          {bg
            ? "Разпознати 8 маркера от PDF (CBC + метаболитен панел). Основни находки: ранен пре-диабет (HbA1c 5.9%, глюкоза 5.8), лека дислипидемия и хронично възпаление (CRP 4.2). Ниско ниво на витамин D корелира с умора."
            : "8 markers parsed from PDF (CBC + metabolic panel). Key findings: early pre-diabetes (HbA1c 5.9%, glucose 5.8), mild dyslipidemia and chronic inflammation (CRP 4.2). Low vitamin D correlates with reported fatigue."}
        </p>
      </div>

      {/* Markers table */}
      <div>
        <h4 className={`text-xs font-bold mb-1.5 ${isDark ? "text-white" : "text-gray-800"}`}>
          {bg ? "Маркери" : "Markers"} ({markers.length})
        </h4>
        <div className="space-y-1">
          {markers.map((m) => (
            <div key={m.name} className={`p-1.5 rounded flex items-center gap-2 ${isDark ? "bg-[hsl(223,47%,10%)]" : "bg-gray-50"}`}>
              <div className="flex-1 min-w-0">
                <p className={`text-[11px] font-medium truncate ${isDark ? "text-gray-200" : "text-gray-800"}`}>{m.name}</p>
                <p className={`text-[9px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                  {bg ? "Норма" : "Ref"}: {m.ref} {m.unit}
                </p>
              </div>
              <div className="text-right">
                <p className={`text-xs font-bold ${isDark ? "text-white" : "text-gray-800"}`}>{m.val} <span className="text-[9px] font-normal">{m.unit}</span></p>
                <span className={`text-[9px] px-1 py-0.5 rounded font-semibold ${statusColor(m.status)}`}>{m.note}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommended actions */}
      <div className={`p-2.5 rounded-lg ${isDark ? "bg-amber-900/20" : "bg-amber-50"}`}>
        <h4 className={`text-xs font-bold mb-1 flex items-center gap-1.5 ${isDark ? "text-amber-400" : "text-amber-700"}`}>
          <AlertTriangle className="w-3.5 h-3.5" />
          {bg ? "Приоритетни действия" : "Priority actions"}
        </h4>
        <ul className="space-y-0.5 pl-4">
          {[
            bg ? "Продължете мониторинг глюкоза 3 месеца — OGTT при влошаване" : "Monitor glucose 3 months — OGTT if worsening",
            bg ? "D3 5000 IU/ден — повторен тест след 8 седмици (цел 50-60 ng/mL)" : "D3 5000 IU/day — retest after 8 weeks (target 50-60 ng/mL)",
            bg ? "Омега-3 + средиземноморска диета за LDL и CRP" : "Omega-3 + Mediterranean diet for LDL and CRP",
          ].map((a, i) => (
            <li key={`act-${i}`} className={`text-[11px] list-disc ${isDark ? "text-amber-200" : "text-amber-800"}`}>
              {a}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function DemoExamplesPanel() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const bg = language === "bg";

  const [active, setActive] = useState("clinical");

  // Auto-open for new users (no completed onboarding)
  const isNewUser = !user?.profile?.completedOnboarding;

  const CTA_CONFIG = {
    clinical: {
      label: bg ? "Пробвайте с Вашите данни" : "Try it with your data",
      testId: "clinical-analysis-card",
      fallback: bg ? "Отворете панел 'Клиничен анализ'" : "Open the Clinical Analysis panel",
    },
    chat: {
      label: bg ? "Попитайте AI сега" : "Ask AI now",
      testId: "chat-interface",
      fallback: bg ? "Отворете AI чата" : "Open the AI chat",
    },
    lab: {
      label: bg ? "Качете Ваше изследване" : "Upload your lab",
      testId: "lab-upload-card",
      fallback: bg ? "Отворете 'Качи изследвания'" : "Open Upload Lab Results",
    },
  };

  const handleTryIt = () => {
    const cfg = CTA_CONFIG[active];
    if (!cfg) return;
    const el = document.querySelector(`[data-testid="${cfg.testId}"]`);
    if (el) {
      // If it's a CollapsiblePanel and collapsed, click the header to open it
      const header = el.querySelector('[role="button"]');
      if (header && el.getAttribute("data-open") !== "true") {
        // Heuristic: look for closed state chevron; just click to toggle
        header.click();
      }
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[#EC4899]", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-[#EC4899]", "ring-offset-2"), 2500);
      }, 150);
    } else {
      toast.info(cfg.fallback);
    }
  };

  return (
    <CollapsiblePanel
      title={bg ? "Виж примерни резултати" : "See Example Results"}
      subtitle={bg ? "Реални AI изходи преди да качите Вашите данни" : "Real AI outputs before you upload your data"}
      icon={Sparkles}
      iconColor="#EC4899"
      credits={0}
      defaultOpen={isNewUser}
      storageKey="demo-examples"
      testId="demo-examples-panel"
    >
      <div className="space-y-3">
        {/* Demo banner */}
        <div className={`p-2 rounded-lg flex items-start gap-2 text-[10px] ${isDark ? "bg-[hsl(223,47%,10%)] text-gray-400" : "bg-gray-50 text-gray-600"}`} data-testid="demo-notice">
          <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0 text-[#EC4899]" />
          <span>
            {bg
              ? "Това са ДЕМО примери, базирани на типични реални случаи. Вашите резултати ще бъдат персонализирани с Вашите данни, генетика и симптоми."
              : "These are DEMO examples based on typical real cases. Your results will be personalized using your data, genetics and symptoms."}
          </span>
        </div>

        {/* Tab selector */}
        <div className="grid grid-cols-3 gap-1.5" data-testid="demo-tabs">
          {TABS.map(tab => {
            const IconTab = tab.icon;
            const isActive = active === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActive(tab.id)}
                className={`flex flex-col items-center gap-1 p-2 rounded-lg text-[10px] font-medium transition-all ${
                  isActive
                    ? "bg-gradient-to-r from-[#EC4899] to-[#6366F1] text-white shadow-sm"
                    : isDark ? "bg-[hsl(223,47%,10%)] text-gray-400 hover:bg-[hsl(223,47%,14%)]" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                data-testid={`demo-tab-${tab.id}`}
              >
                <IconTab className="w-3.5 h-3.5" />
                <span className="leading-tight text-center">{bg ? tab.bg : tab.en}</span>
              </button>
            );
          })}
        </div>

        {/* Active demo content */}
        <div className="min-h-[200px]">
          {active === "clinical" && <ClinicalDemo bg={bg} isDark={isDark} />}
          {active === "chat" && <ChatDemo bg={bg} isDark={isDark} />}
          {active === "lab" && <LabDemo bg={bg} isDark={isDark} />}
        </div>

        {/* Primary CTA — "Try it with your data" */}
        <Button
          onClick={handleTryIt}
          className="w-full h-10 bg-gradient-to-r from-[#EC4899] via-[#A855F7] to-[#6366F1] hover:opacity-90 text-white text-xs font-semibold shadow-md group"
          data-testid={`demo-try-it-${active}`}
        >
          <Sparkles className="w-3.5 h-3.5 mr-1.5" />
          {CTA_CONFIG[active].label}
          <ArrowRight className="w-3.5 h-3.5 ml-1.5 transition-transform group-hover:translate-x-0.5" />
        </Button>

        {/* Evidence sources attribution */}
        <div className={`flex items-center gap-2 text-[9px] pt-2 border-t ${isDark ? "border-[hsl(223,47%,15%)] text-gray-500" : "border-gray-100 text-gray-400"}`}>
          <BookOpen className="w-3 h-3" />
          <span>
            {bg
              ? "Научни източници: PubMed · Semantic Scholar · RCT мета-анализи"
              : "Scientific sources: PubMed · Semantic Scholar · RCT meta-analyses"}
          </span>
          <ExternalLink className="w-2.5 h-2.5 ml-auto" />
        </div>
      </div>
    </CollapsiblePanel>
  );
}
